/* eslint-disable @typescript-eslint/no-explicit-any */
import Head from 'next/head';
import 'react-tooltip/dist/react-tooltip.css';
import {
  BannerCTASkeleton,
  CompareCardsSkeleton,
  FAQSSkeleton,
  FeaturesSkeleton,
  SunriseBannerSkeleton,
  HeroSkeleton,
  MemberRewardsSkeleton,
  Section04Skeleton,
  HyperlinkBannerSkeleton,
  TestimonialsSkeleton,
  Section02Skeleton,
  Section01Skeleton,
} from '~/components/shared/Skeleton';

import MonarchSection from '~/cohesion/Monarch/MonarchSection';
import { MonarchSlot, useMonarchRule } from '@redventures/cohesion-utils-react';
import { lazy, useEffect } from 'react';
import FuelPromotion from '~/components/MonarchCalls/DoubleFuel';
import useBeacon from './beaconEvent';
import { WALMART_URLS } from '../config/urls';
import { useVariantIds } from '~/lib/hooks/useVariantIds';

// Dynamic imports are not working with our skeletons with <MonarchSlot> due to Suspense overriding
// Once fix is implemented from RP team, dynamically import these components
const PromotionCTABanner = lazy(
  () => import('~/components/Layout/PromotionCTABanner')
);
const SimplifiedSolidHero = lazy(
  () => import('~/components/editable/SimplifiedSolidHero')
);
const BenefitsTile = lazy(
  () => import('~/components/editable/BenefitsTileCarousel')
);
const MemberFilterV2 = lazy(
  () => import('~/components/editable/MemberRewardsFilterV2')
);
const LifestyleTestimonials = lazy(
  () => import('~/components/editable/LifestyleTestimonials')
);
const AssistBanner = lazy(() => import('~/components/Layout/AssistBanner'));
const CompareTable = lazy(() => import('~/components/editable/CompareTableV2'));
const FAQ = lazy(() => import('~/components/Layout/FAQ'));
const Layout = lazy(() => import('~/components/Layout'));

// type any: using type unknown or other value breaks
export type MonarchEvaluationsValue = { [key: string]: any };

export default function Home() {
  // Call the custom hook
  useBeacon();

  const doublefuel = useMonarchRule<MonarchEvaluationsValue>('doublefuel');
  const fuelData = doublefuel?.value?.data;

  const { isDarkMode } = useVariantIds();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isDarkMode) {
      document.body.classList.add('bg-dark-mode-background');
    }
  }, [isDarkMode]);

  console.log('BasePage');

  return (
    <>
      <Head>
        <title>Walmart+</title>
      </Head>
      <div>
        <Layout signInButton={true} url={WALMART_URLS.PLUS_PLANS}>
          {/* Hero */}
          <MonarchSection elementPosition='HERO'>
            <MonarchSlot fallback={<HeroSkeleton />} slotId='hero'>
              <SimplifiedSolidHero />
            </MonarchSlot>
          </MonarchSection>
          <span id='trigger-hov-cta' />

          {/* Section 01 */}
          <MonarchSection elementPosition='SECTION_01'>
            <MonarchSlot
              fallback={<Section01Skeleton />}
              slotId='section01'
            ></MonarchSlot>
          </MonarchSection>

          {/* Section 02 */}
          <MonarchSection elementPosition='SECTION_02'>
            <MonarchSlot
              fallback={<Section02Skeleton />}
              slotId='section02'
            ></MonarchSlot>
          </MonarchSection>

          {/* Section 03 */}
          {fuelData ? (
            <FuelPromotion />
          ) : (
            <MonarchSection elementPosition='SECTION_03'>
              <MonarchSlot slotId='section03'></MonarchSlot>
            </MonarchSection>
          )}

          {/* Section 04 */}
          <MonarchSection elementPosition='SECTION_04'>
            <MonarchSlot slotId='section04' fallback={<Section04Skeleton />}>
              <PromotionCTABanner />
            </MonarchSlot>
          </MonarchSection>

          {/* Section 05 */}
          <MonarchSection elementPosition='SECTION_05'>
            <MonarchSlot
              fallback={<HyperlinkBannerSkeleton />}
              slotId='section05'
            >
              <AssistBanner />
            </MonarchSlot>
          </MonarchSection>

          {/* Section 06 */}
          <MonarchSection elementPosition='SECTION_06'>
            <MonarchSlot fallback={<TestimonialsSkeleton />} slotId='section06'>
              <BenefitsTile />
            </MonarchSlot>
          </MonarchSection>

          {/* Section 07 */}
          <MonarchSection elementPosition='SECTION_07'>
            <MonarchSlot fallback={<FeaturesSkeleton />} slotId='section07'>
              <MemberFilterV2 />
            </MonarchSlot>
          </MonarchSection>

          {/* Section 08 */}
          <MonarchSection elementPosition='SECTION_08'>
            <MonarchSlot
              fallback={<MemberRewardsSkeleton />}
              slotId='section08'
            >
              <LifestyleTestimonials />
            </MonarchSlot>
          </MonarchSection>

          {/* Section 09 */}
          <MonarchSection elementPosition='SECTION_09'>
            <MonarchSlot
              fallback={<BannerCTASkeleton />}
              slotId='section09'
            ></MonarchSlot>
          </MonarchSection>

          {/* Section 10 */}
          <MonarchSection elementPosition='SECTION_10'>
            <MonarchSlot fallback={<CompareCardsSkeleton />} slotId='section10'>
              <CompareTable />
            </MonarchSlot>
          </MonarchSection>

          {/* Section 11 */}
          <MonarchSection elementPosition='SECTION_11'>
            <MonarchSlot
              fallback={<SunriseBannerSkeleton />}
              slotId='section11'
            ></MonarchSlot>
          </MonarchSection>

          {/* Section 12 */}
          <MonarchSection elementPosition='SECTION_12'>
            <MonarchSlot fallback={<FAQSSkeleton />} slotId='section12'>
              <FAQ />
            </MonarchSlot>
          </MonarchSection>
        </Layout>
      </div>
    </>
  );
}
